import React, { useEffect, useState } from 'react';

type Props = {
  symbol: string;
  targetWeightPct: number | null;
  weightPinned?: boolean;
  disabled?: boolean;
  onSave: (input: { targetWeightPct?: number; weightPinned?: boolean }) => Promise<void>;
};

/**
 * Editable Target % + pin toggle for deploy-list priority.
 * Saving a weight auto-pins (backend); pin alone toggles overwrite protection.
 */
export const DeployTargetWeightCell: React.FC<Props> = ({
  symbol,
  targetWeightPct,
  weightPinned = false,
  disabled = false,
  onSave
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(
    targetWeightPct != null ? String(targetWeightPct) : ''
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(targetWeightPct != null ? String(targetWeightPct) : '');
    }
  }, [targetWeightPct, editing]);

  const saveWeight = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 0.1 || n > 50) {
      setDraft(targetWeightPct != null ? String(targetWeightPct) : '');
      setEditing(false);
      return;
    }
    if (targetWeightPct != null && Math.abs(n - targetWeightPct) < 0.005) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave({ targetWeightPct: Math.round(n * 100) / 100 });
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const togglePin = async () => {
    setBusy(true);
    try {
      await onSave({ weightPinned: !weightPinned });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex flex-col items-end gap-1">
      <div className="flex items-center justify-end gap-1.5">
        {editing ? (
          <input
            type="number"
            inputMode="decimal"
            min={0.1}
            max={50}
            step={0.1}
            autoFocus
            disabled={busy || disabled}
            value={draft}
            onChange={(ev) => setDraft(ev.target.value)}
            onBlur={() => void saveWeight()}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                void saveWeight();
              }
              if (ev.key === 'Escape') {
                setDraft(targetWeightPct != null ? String(targetWeightPct) : '');
                setEditing(false);
              }
            }}
            className="w-16 min-h-[36px] rounded border border-kib-cyber/40 bg-black/40 px-1.5 py-1 text-right font-mono text-sm text-kib-fg"
            aria-label={`Target weight % for ${symbol}`}
          />
        ) : (
          <button
            type="button"
            disabled={busy || disabled}
            onClick={() => setEditing(true)}
            className="min-h-[36px] rounded px-1 font-mono tabular-nums text-kib-cyber hover:bg-white/[0.06] hover:underline disabled:opacity-50"
            title="Edit target weight (pins automatically)"
          >
            {targetWeightPct != null ? `${targetWeightPct}%` : '—'}
          </button>
        )}
        <button
          type="button"
          disabled={busy || disabled}
          onClick={() => void togglePin()}
          className={`min-h-[36px] min-w-[36px] rounded px-1.5 text-[10px] font-semibold uppercase tracking-wide disabled:opacity-50 ${
            weightPinned
              ? 'bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/30'
              : 'bg-white/[0.04] text-kib-muted hover:bg-white/[0.08] hover:text-kib-fg'
          }`}
          title={
            weightPinned
              ? 'Pinned — auto-refresh will not overwrite Target %'
              : 'Pin Target % so auto-refresh / recompute skip this name'
          }
          aria-pressed={weightPinned}
          aria-label={weightPinned ? `Unpin ${symbol} target` : `Pin ${symbol} target`}
        >
          {weightPinned ? 'Unpin' : 'Pin'}
        </button>
      </div>
    </div>
  );
};
